import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import { AppProvider } from './context/AppContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { CartProvider } from './contexts/CartContext';
import { PaymentProvider } from './contexts/PaymentContext';
import AppLayout from './components/AppLayout';
import LandingPage from './components/LandingPage';
import DevoteeSearch from './components/DevoteeSearch';
import NewDevoteeForm from './components/NewDevoteeForm';
import ProductsPage from './components/ProductsPage';
import KotilingaPage from './components/KotilingaPage';
import ProductSearchNew from './components/ProductSearchNew';
import ProductVariationsScreen from './components/ProductVariationsScreen';
import CartPage from './components/CartPage';
import TestWooCommerce from './components/TestWooCommerce';
import PaymentPage from './components/PaymentPage';
import PaymentTest from './components/test/PaymentTest';
import ConfirmationPage from './pages/ConfirmationPage';
import HomeButton from './components/HomeButton';
import KioskInitialize from './pages/KioskInitialize';
import StartSplash from './pages/StartSplash';
import './index.css';
// Load keyboard styles globally to avoid route/component-level CSS timing issues.
import 'simple-keyboard/build/css/index.css';
import './components/common/keyboard/alphaModern.css';
import { SessionTimeoutProvider } from './context/SessionTimeoutContext';
import { KeyboardProvider, KeyboardOverlay } from './context/KeyboardContext';
// Import keyboard components in main bundle so production (VM) never fails on lazy chunk load
import NumericKeypad from './components/common/keyboard/NumericKeypad';
import AlphaKeyboard from './components/common/keyboard/AlphaKeyboard';

// Kiosk hardening: block zoom shortcuts and gestures
(() => {
  if (typeof window === 'undefined') return;
  // Prevent Ctrl/Cmd +/-/0 zoom
  window.addEventListener('keydown', (e) => {
    const isCtrlLike = e.ctrlKey || e.metaKey;
    if (!isCtrlLike) return;
    const k = e.key.toLowerCase();
    if (k === '+' || k === '=' || k === '-' || k === '_' || k === '0') {
      e.preventDefault();
      e.stopPropagation();
    }
  });
  // Prevent Ctrl+wheel zoom
  window.addEventListener('wheel', (e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, { passive: false });
  // iOS Safari gesture events
  ['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => {
    window.addEventListener(type, (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  });
})();

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a237e',
      light: '#534bae',
      dark: '#000051',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff6d00',
      light: '#ff9e40',
      dark: '#c43c00',
      contrastText: '#000000',
    },
    background: {
      default: '#f5f5f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#4a4a4a',
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
      color: '#1a237e',
    },
    h2: {
      fontWeight: 500,
      color: '#1a237e',
    },
    h3: {
      fontWeight: 500,
      color: '#1a237e',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiCircularProgress: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontWeight: 600,
          letterSpacing: 0.2,
          transition: 'all 0.15s ease-in-out',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            transition: 'border-color 0.15s ease-in-out',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1a237e',
            borderWidth: 2,
          },
        },
        input: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },
});

// Wrapper component to handle initial navigation
const InitialNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  React.useEffect(() => {
    // Preserve query parameters when navigating to home
    const searchParams = location.search || '';
    navigate(`/home${searchParams}`, { replace: true });
  }, [navigate, location.search]);
  
  return <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh' 
  }}>
    <CircularProgress />
  </div>;
};

// Wrapper component to handle LandingPage with navigation
const LandingPageWrapper = () => {
  const navigate = useNavigate();
  
  const handleDonateClick = () => {
    try {
      console.log('Navigating to checkout');
      navigate('/checkout');
    } catch (error) {
      console.error('Navigation error:', error);
      window.location.href = '/checkout'; // Fallback navigation
    }
  };
  
  return <LandingPage onDonateClick={handleDonateClick} />;
};

// Wrapper component to provide navigation to SessionProvider
const SessionProviderWrapper = ({ children }) => {
  const navigate = useNavigate();
  return (
    <SessionProvider navigate={navigate}>
      {children}
    </SessionProvider>
  );
};

// Get the root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  console.error('Failed to find the root element');
} else {
  console.log('Root element found, creating React root');
  try {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <KeyboardProvider NumericKeypadComponent={NumericKeypad} AlphaKeyboardComponent={AlphaKeyboard}>
            <AppProvider>
              <Router>
                <SessionTimeoutProvider>
                <SessionProviderWrapper>
                  <CartProvider>
                    <PaymentProvider>
                      <Routes>
                      <Route path="/initialize" element={<KioskInitialize />} />
                      <Route path="/start" element={<StartSplash />} />
                      <Route path="/home" element={<LandingPageWrapper />} />
                      <Route element={<AppLayout />}>
                        <Route path="/" element={<InitialNavigation />} />
                        <Route path="/shop" element={<ProductsPage />} />
                        <Route path="/kotilinga" element={<KotilingaPage />} />
                        <Route path="/cart" element={<CartPage />} />
                        <Route path="/checkout" element={<DevoteeSearch />} />
                        <Route path="/checkout/new-devotee" element={<NewDevoteeForm />} />
                        <Route path="/payment" element={<PaymentPage />} />
                        <Route path="/test-woocommerce" element={<TestWooCommerce />} />
                        <Route path="/test-payment" element={<PaymentTest />} />
                        <Route path="/confirmation" element={<ConfirmationPage />} />
                      </Route>
                      </Routes>
                    </PaymentProvider>
                  </CartProvider>
                </SessionProviderWrapper>
                </SessionTimeoutProvider>
              </Router>
              {/* Global keyboard overlay */}
              <React.Suspense fallback={null}>
                <KeyboardOverlay />
              </React.Suspense>
            </AppProvider>
          </KeyboardProvider>
        </ThemeProvider>
      </React.StrictMode>
    );
    console.log('React root rendered successfully');
    // Warm the daily catalog/donor caches in the background (fire-and-forget; never throws).
    // Primary reads stay live; these snapshots are only used as a fallback on network failure.
    import('./utils/cacheWarm')
      .then(({ warmCatalogCaches }) => warmCatalogCaches())
      .catch((e) => console.warn('[cacheWarm] load/run failed (ignored):', e?.message || e));
  } catch (error) {
    console.error('Error rendering React app:', error);
  }
}
